import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, apiFetchBlob } from '@/lib/api/client';
import { readAccessToken } from '@/lib/auth/token';

/**
 * 마이페이지 — 본인 정보와 프로필 사진 (V373 · 사용자 결정 2026-09-30).
 *
 * <p>세션(`useSession`)과 갈라 둔다. 세션은 모든 화면이 매번 묻는 것이라 가볍게 두고, 사진·보직은
 * 마이페이지와 아바타만 본다.
 */

export type Profile = {
  name: string;
  email: string;
  /** 직원은 null — 포털이 직원 전화를 들고 있지 않다. 화면은 null 이면 그 줄을 안 그린다. */
  phone: string | null;
  partyType: string;
  partyName: string;
  /** 현장에서의 보직. 비면 미지정. 직원은 null(인사 직위가 따로 있다). */
  positionTitle: string | null;
  hasPhoto: boolean;
};

export const profileQueryKey = ['external', 'profile'] as const;

const photoQueryKey = ['external', 'profile', 'photo'] as const;

export function useMyProfile() {
  return useQuery({
    queryKey: profileQueryKey,
    queryFn: () => apiFetch<Profile>('/me'),
    enabled: readAccessToken() !== null,
    staleTime: 60_000,
  });
}

/**
 * 내 프로필 사진 — 오브젝트 URL.
 *
 * <p>`<img src>` 에 `/api/external/me/photo` 를 바로 넣을 수 없다. 이 앱은 Bearer 토큰이라
 * 브라우저가 이미지 요청에 인증을 싣지 않는다(쿠키가 아니다).
 *
 * <p>`usePhotoUrl` 과 달리 **react-query 로 한 번만 받아 공유한다** — 아바타가 머리줄·관문·
 * 마이페이지 셋에서 동시에 서므로 컴포넌트마다 받으면 같은 바이트를 세 번 가져온다.
 *
 * <p>사진이 없으면 서버가 404 다. 그때 이 훅은 **null 을 준다** — 화면은 이름 첫 글자로 물러선다
 * (`Avatar`). 「사진 없음」은 오류가 아니므로 재시도하지 않는다.
 */
export function useMyPhotoUrl(hasPhoto: boolean): string | null {
  const blob = useQuery({
    queryKey: photoQueryKey,
    queryFn: () => apiFetchBlob('/me/photo'),
    enabled: hasPhoto && readAccessToken() !== null,
    retry: false,
    staleTime: Infinity,
  });

  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const data = blob.data;
    if (data === undefined) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob.data]);

  return url;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (positionTitle: string) =>
      apiFetch<Profile>('/me', { method: 'PUT', body: { positionTitle } }),
    onSuccess: (next) => queryClient.setQueryData(profileQueryKey, next),
  });
}

/**
 * 사진 올리기 · 내리기.
 *
 * <p>성공하면 사진 질의를 **지운다**(`removeQueries`) — 무효화만 하면 옛 Blob 이 캐시에 남아
 * 바꾼 직후 옛 얼굴이 한 번 더 보인다. 키가 하나뿐인 자리라 지우는 것이 맞다.
 */
export function useUploadMyPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return apiFetch<Profile>('/me/photo', { method: 'POST', body });
    },
    onSuccess: (next) => {
      queryClient.removeQueries({ queryKey: photoQueryKey });
      queryClient.setQueryData(profileQueryKey, next);
    },
  });
}

export function useDeleteMyPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<Profile>('/me/photo', { method: 'DELETE' }),
    onSuccess: (next) => {
      queryClient.removeQueries({ queryKey: photoQueryKey });
      queryClient.setQueryData(profileQueryKey, next);
    },
  });
}
